/**
 * BottomLine — Layer 3 of the restructured analysis panel.
 *
 * Three cells: Build Feasibility · CAPEX Range · Blockers/Concerns/Gaps.
 * The third cell's numbers are clickable (audit 2026-06-01): each digit
 * opens a dropdown listing the underlying items, and clicking an item
 * jumps to the corresponding row in the Data tab.
 */

import React, { useEffect, useRef, useState } from 'react';
import { formatDistanceKm } from '../utils/format';
import { createPortal } from 'react-dom';

const PLANT_CAPEX_MNOK_PER_MW = {
  data_center: 50,
  hydrogen:    10,
};

const THEME_LABEL = {
  power:          'Power & Grid',
  planning:       'Planning & Zoning',
  water:          'Water Access',
  site_formation: 'Site Formation',
  access:         'Road Access',
  fiber:          'Network Connectivity',
  environment:    'Environmental Risk',
  permitting:     'Permitting Outlook',
  expansion:      'Expansion Potential',
  // ammonia_v1 index themes
  grid:           'Grid Connection',
  port:           'Port & Export',
  power_price:    'Power Price',
  land:           'Land & Platform',
  setback:        'Setback & Protected Areas',
};

function deriveBottomLine(site, useCase, projectMw) {
  // 1. Hard stops — fields that should have rejected the site but are
  // surfaced anyway when the site survived the SSE filter (e.g. flood
  // edge that snuck through, or a salmon-river adjacency loaded from a
  // saved enrichment). Each entry carries its field for jump-to.
  const hardStopItems = [
    (site.flood_hard_stop || site.flood_zone)
      ? { label: 'Flood zone', field: 'flood_zone' } : null,
    (site.is_salmon_river || site.has_salmon)
      ? { label: 'Salmon river within 500 m', field: 'is_salmon_river' } : null,
    site.nin_type_a
      ? { label: 'NiN type A habitat', field: 'nin_type_a' } : null,
    site.har_kulturminne
      ? { label: 'Protected cultural monument', field: 'har_kulturminne' } : null,
  ].filter(Boolean);

  const concerns = Array.isArray(site.concerns) ? site.concerns : [];
  const concernItems = concerns.map(c => ({
    label: c.message?.split(' — ')[0] || c.type || 'Concern',
    field: c.field || null,
    severity: c.severity || 'medium',
  }));

  // Gaps: readiness themes flagged defaulted by the backend. Targets
  // MissingData via the "gap:<theme>" prefix — that's the section that
  // explains the absence and prescribes the action. (Pointing these at
  // VerifiedSiteData rows didn't work: a theme is flagged defaulted
  // precisely *because* the underlying field is missing, so the target
  // row often doesn't render.)
  const themes = site.readiness_themes || {};
  const gapItems = Object.entries(themes)
    .filter(([, t]) => t?.defaulted)
    .map(([key]) => ({
      label: THEME_LABEL[key] || key,
      field: `gap:${key}`,
    }));

  // ── Build feasibility (unchanged) ────────────────────────────────
  const highConcerns = concerns.filter(c => c.severity === 'high');
  let buildable;
  if (hardStopItems.length > 0) {
    buildable = { verdict: 'No', color: '#dc2626',
                  reason: `Blocker: ${hardStopItems[0].label.toLowerCase()}`,
                  dotClass: 'status-blocker' };
  } else if (highConcerns.length > 0) {
    buildable = { verdict: 'Conditional', color: '#d97706',
                  reason: highConcerns[0].message.split(' — ')[0],
                  dotClass: 'status-concern' };
  } else if (site.readiness_score != null && site.readiness_score < 50) {
    buildable = { verdict: 'Conditional', color: '#d97706',
                  reason: 'Below readiness threshold across multiple themes',
                  dotClass: 'status-concern' };
  } else {
    buildable = { verdict: 'Yes', color: '#16a34a',
                  reason: 'No blockers identified',
                  dotClass: 'status-strong' };
  }

  // ── CAPEX (unchanged) ────────────────────────────────────────────
  const grid    = Number(site.grid_capex_mnok || 0);
  const upg     = Number(site.voltage_upgrade_mnok || 0);
  const waterKm = site.water_distance_km ?? site.nearest_water_distance_km;
  const water   = waterKm != null ? Number(waterKm) * 10 : 0;   // 10 MNOK/km (matches lcoh_service)
  const roadKm  = site.nearest_road_distance_km;
  const road    = roadKm != null ? Number(roadKm) * 20 : 0;
  const perMw   = PLANT_CAPEX_MNOK_PER_MW[useCase === 'data_center' ? 'data_center' : 'hydrogen'];
  const mw      = Number(projectMw || 0);
  const plant   = perMw * mw;
  const totalMid = grid + upg + water + road + plant;
  const capex = totalMid > 0
    ? { midMnok: Math.round(totalMid),
        lowMnok: Math.round(totalMid * 0.75),
        highMnok: Math.round(totalMid * 1.25) }
    : null;

  return {
    buildable, capex,
    hardStopItems, concernItems, gapItems,
  };
}

// ── Clickable counter with dropdown ─────────────────────────────────
function CounterWithItems({ count, items, color, dotClass, onJump }) {
  const btnRef = useRef(null);
  const [anchor, setAnchor] = useState(null);

  useEffect(() => {
    if (!anchor) return;
    const close = () => setAnchor(null);
    const onClick = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest('.bl-pop')) return;
      close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor]);

  if (count === 0 || !items.length) {
    // No items → render plain digit, no click target
    return <span style={{ color, opacity: 0.7 }}>{count}</span>;
  }

  // Single item → jump directly on click (no menu)
  // Multiple items → open dropdown
  const onToggle = (e) => {
    e.stopPropagation();
    if (items.length === 1 && items[0].field) {
      onJump?.(items[0].field);
      return;
    }
    if (anchor) { setAnchor(null); return; }
    setAnchor(e.currentTarget.getBoundingClientRect());
  };

  return (
    <>
      <button
        ref={btnRef} type="button" onClick={onToggle}
        title={items.length === 1
          ? `Jump to ${items[0].label}`
          : `${count} item(s) — click to see them`}
        style={{
          background: 'none', border: 'none', padding: '0 2px',
          color, fontWeight: 600, fontFamily: 'inherit',
          fontSize: 'inherit', cursor: 'pointer',
          textDecoration: 'underline', textDecorationStyle: 'dotted',
          textUnderlineOffset: 2,
        }}>
        {count}
      </button>
      {anchor && createPortal(
        <div className="bl-pop"
             style={{
               position: 'fixed',
               top: anchor.bottom + 6,
               left: Math.max(8, Math.min(window.innerWidth - 280, anchor.left - 8)),
               width: 280, maxHeight: '60vh', overflowY: 'auto',
               background: '#0b1220', border: '1px solid #334155',
               borderRadius: 8, padding: 4,
               boxShadow: '0 16px 40px rgba(0,0,0,0.6)',
               zIndex: 10000, fontSize: 12,
             }}
             role="menu">
          {items.map((it, i) => (
            <button
              key={i}
              onClick={(e) => {
                e.stopPropagation();
                if (it.field) onJump?.(it.field);
                setAnchor(null);
              }}
              disabled={!it.field}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                background: 'none', border: 'none', padding: '8px 12px',
                color: '#e2e8f0', fontSize: 12, lineHeight: 1.4,
                cursor: it.field ? 'pointer' : 'not-allowed',
                opacity: it.field ? 1 : 0.5,
                borderRadius: 4,
              }}
              onMouseEnter={(e) => { if (it.field) e.currentTarget.style.background = '#1e293b'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              title={it.field ? `Jump to ${it.field} in Data tab` : 'No data field linked'}>
              <span style={{ marginRight: 6 }}>→</span>{it.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}

export default function BottomLine({ site, useCase, projectMw, onJumpToField }) {
  if (!site) return null;
  const { buildable, capex, hardStopItems, concernItems, gapItems } =
    deriveBottomLine(site, useCase, projectMw);

  // minWidth:0 lets flex cells shrink instead of overflowing — without
  // it, a long value ("Transformatorstasjon · 66 kV") pushes the
  // right-hand cell off-panel. `valueFontSize` lets cells with long
  // string values opt down from the default 18 px to a more compact
  // size that fits in a tight column.
  const Cell = ({ label, value, color, sub, dotClass, mono, valueFontSize = 18 }) => (
    <div style={{
      flex: 1, minWidth: 0, padding: '8px 12px',
      borderRight: '1px solid #1f2937',
    }}>
      <div className="section-header" style={{ marginBottom: 4 }}>{label}</div>
      <div style={{
        fontSize: valueFontSize, fontWeight: 600, color, marginTop: 2,
        display: 'flex', alignItems: 'center', gap: 4,
        wordBreak: 'break-word', lineHeight: 1.25,
      }}>
        {dotClass && <span className={`status-dot ${dotClass}`} />}
        <span className={mono ? 'numeric' : ''}>{value}</span>
      </div>
      {sub && (
        <div style={{
          fontSize: 11, opacity: 0.75, marginTop: 4,
          wordBreak: 'break-word', lineHeight: 1.4,
        }}>{sub}</div>
      )}
    </div>
  );

  // Substation summary for the header (replaces CAPEX Range cell —
  // CAPEX still lives in the Estimates section below). Pulls from
  // _capacityContext when the capacity-first chain ran; falls back
  // to the local-table nearest_grid_* fields otherwise. Same
  // unnamed-fallback shape used elsewhere — operator + voltage +
  // station_id last-6 when name is "Unknown".
  const _cc = site._capacityContext;
  const _subRawName = _cc?.name || site.nearest_grid_name || site.nearest_grid?.name;
  const _subKv = _cc?.voltage_kv ?? site.nearest_grid_voltage_kv ?? site.voltage_level_kv;
  const _subKm = _cc?.distance_km ?? site.nearest_grid_distance_km;
  const _subOp = _cc?.operator || site.nearest_grid_operator;
  const _subUseless = !_subRawName || /^(unknown|null|none|n\/a)$/i.test(String(_subRawName).trim());
  const _subName = _subUseless
    ? (() => {
        const parts = [];
        if (_subOp) parts.push(_subOp);
        if (_subKv) parts.push(`${_subKv} kV`);
        if (_cc?.station_id) parts.push(`#${String(_cc.station_id).slice(-6)}`);
        return parts.length > 0 ? `Unnamed — ${parts.join(' · ')}` : 'Unknown';
      })()
    : _subRawName;
  const subValue = _subName + (_subKv ? ` · ${_subKv} kV` : '');
  const _tier = _cc?.tier_classification;
  const _tierLabel = ({
    sufficient:        'Confirmed capacity',
    partial:           `Partial · ${_cc?.available_cons_mw ?? '?'} MW available`,
    capacity_inquiry:  'Capacity inquiry needed',
    voltage_overkill:  'Above voltage · step-down',
    voltage_upgrade:   'Below voltage · upgrade',
    saturated:         'Saturated · no capacity',
  })[_tier];
  const _tierColor = ({
    sufficient:        '#4ade80',
    partial:           '#fbbf24',
    capacity_inquiry:  '#60a5fa',
    voltage_overkill:  '#c4b5fd',
    voltage_upgrade:   '#cbd5e1',
    saturated:         '#f87171',
  })[_tier] || '#7dd3fc';
  // Use the shared distance formatter so the panel matches CompareTable +
  // SelectedSiteCard exactly (parity contract — see displaySchema.js).
  const _subKmNum = typeof _subKm === 'number' ? _subKm : parseFloat(_subKm);
  const subSub = Number.isFinite(_subKmNum)
    ? `${formatDistanceKm(_subKmNum)} away${_tierLabel ? ` · ${_tierLabel}` : ''}`
    : (_tierLabel || 'Awaiting enrichment');

  const blockers = hardStopItems.length;
  const conc     = concernItems.length;
  const gaps     = gapItems.length;
  const issueColor = blockers > 0 ? '#dc2626'
                   : conc > 0     ? '#d97706'
                   :                '#16a34a';
  const issueDot   = blockers > 0 ? 'status-blocker'
                   : conc > 0     ? 'status-concern'
                   :                'status-strong';

  return (
    // BottomLine intentionally keeps a 0 horizontal padding so the
    // three cells span the full panel width. The .overview-tab gap
    // (18 px from AnalysisPanel.css) handles the vertical rhythm —
    // border-bottom dropped 2026-06-08 to avoid double dividers
    // when neighbouring sections also have their own chrome.
    <div style={{
      display: 'flex',
      padding: '6px 0',
      background: 'rgba(255,255,255,0.015)',
    }}>
      <Cell label="Build Feasibility"
            value={buildable.verdict}
            color={buildable.color}
            dotClass={buildable.dotClass}
            sub={buildable.reason} />
      <Cell label="Nearest Substation"
            value={subValue}
            color={_tierColor}
            mono={false}
            valueFontSize={14}
            sub={subSub} />
      {/* Issue summary — each digit is clickable. Single item jumps
          straight to the Data row; multiple items open a dropdown
          listing them so the user can pick which row to inspect. */}
      <div style={{ flex: 1, minWidth: 0, padding: '8px 12px' }}>
        <div className="section-header" style={{ marginBottom: 4 }}>Blockers / Concerns / Gaps</div>
        <div className="numeric" style={{ fontSize: 18, fontWeight: 600, color: issueColor, marginTop: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <span className={`status-dot ${issueDot}`} />
          <CounterWithItems count={blockers} items={hardStopItems} color={issueColor} onJump={onJumpToField} />
          <span style={{ opacity: 0.5 }}> / </span>
          <CounterWithItems count={conc}     items={concernItems}  color={issueColor} onJump={onJumpToField} />
          <span style={{ opacity: 0.5 }}> / </span>
          <CounterWithItems count={gaps}     items={gapItems}      color={issueColor} onJump={onJumpToField} />
        </div>
        <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4 }}>
          hard / soft / missing data
          {(blockers + conc + gaps) > 0 && <span style={{ marginLeft: 6, opacity: 0.8 }}>· click any number</span>}
        </div>
      </div>
    </div>
  );
}
